import React, { useCallback, useEffect, useState } from 'react'
import { Grid, LinearProgress } from '@mui/material'
import { useFormContext, UseFormSetValue } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import useAuth from '../../../../hooks/useAuth'
import IssueApi from '../../../../api/IssueApi'
import IssuesGroupApi from '../../../../api/IssuesGroupApi'
import ConvertiblesApi from '../../../../api/ConvertiblesApi'
import { AllCompanyIssues } from './AllCompanyIssues'
import { IssueEntity } from 'types/IssueEntity'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { IssuesGroupEntity } from 'types/IssuesGroupEntity'
import { IssueGroups } from './IssueGroups'
import { FormIssueEntity } from '../../types/FormIssueEntity'
import { FormConvertibleEntity } from '../../types/FormConvertibleEntity'

/**
 * Takes a list of entities with groupId attributes and returns an object with the entities grouped by groupId.
 */
const byGroupId = <T extends { groupId: string }>(entities: Array<T>) =>
    entities?.reduce(
        (acc, entity) => {
            if (!acc[entity.groupId]) acc[entity.groupId] = []
            acc[entity.groupId].push(entity)
            return acc
        },
        {} as Record<string, T[]>,
    ) || {}

const flattenGroupList = <T extends object>(issueGroups: Record<string, T[]>) => {
    return Object.values(issueGroups).flat()
}

const isGroupInList = (issueGroups: Array<Array<{ id: string }>>, issuesGroup: Array<{ id: string }>) => {
    return issueGroups.some((g) => {
        if (g.length !== issuesGroup.length) return false
        return g.every((i) => issuesGroup.some((j) => i.id === j.id))
    })
}

const useSetFormGroups = (setValue: UseFormSetValue<FormIssueEntity | FormConvertibleEntity>) => {
    const setFormGroups = useCallback(
        (groups: Record<string, Array<Omit<FormIssueEntity['issueGroups'][number], 'type'> & { type?: string }>>) => {
            setValue(
                'issueGroups',
                flattenGroupList(groups).map(
                    (group) =>
                        ({
                            groupId: group.groupId,
                            issueId: group.issueId || null,
                            convertibleId: group.convertibleId || null,
                            type: group.type || 'convertible',
                        }) as FormIssueEntity['issueGroups'][number],
                ),
            )
        },
        [setValue], // Add setValue as a dependency
    )

    return { setFormGroups }
}

const issueApi = new IssueApi()
const convertibleApi = new ConvertiblesApi()
const issuesGroupApi = new IssuesGroupApi()

function LinkedIssuesDetails({
    entity,
    entityType,
    setIsCustomFormDirty,
}: {
    setIsCustomFormDirty: (v: boolean) => void
} & (
    | {
          entityType: 'issue'
          entity: Omit<IssuesGroupEntity<'issue'>, 'groupId' | 'issueId' | 'convertibleId'>
      }
    | {
          entityType: 'convertible'
          entity: Omit<IssuesGroupEntity<'convertible'>, 'groupId' | 'issueId' | 'convertibleId'>
      }
)) {
    const { isAuthenticated } = useAuth()
    const { setValue } = useFormContext<FormIssueEntity | FormConvertibleEntity>()
    const { setFormGroups } = useSetFormGroups(setValue)

    const [issues, setIssues] = useState<IssueEntity[] | null>(null)
    const [convertibles, setConvertibles] = useState<ConvertibleEntity[] | null>(null)
    const [issueGroups, setIssueGroups] = useState<Record<string, IssuesGroupEntity[]> | null>(null)
    const companyId = entity.companyId

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !companyId) return
            let issues = await issueApi.findByCompanyId(companyId, false)
            issues = issues.filter((i) => i.id !== entity?.id)
            setIssues(issues)
        }
        void fetchData()
    }, [isAuthenticated, companyId, entity])

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !companyId) return
            let convertibles = await convertibleApi.findByCompanyId(companyId)
            convertibles = convertibles.filter((c) => c.id !== entity.id)
            setConvertibles(convertibles)
        }
        void fetchData()
    }, [isAuthenticated, companyId, entity.id])

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !companyId) return
            const issueGroups = await issuesGroupApi.findByCompanyId(companyId)
            const groups = byGroupId(issueGroups)
            setFormGroups(groups)
            setIssueGroups(groups)
        }
        void fetchData()
    }, [setIssueGroups, setFormGroups, isAuthenticated, companyId])

    if (!issues || !issueGroups) return <LinearProgress />

    const handleCreateGroup = (entities: Array<ConvertibleEntity | IssueEntity>) => {
        const groupId = uuidv4()
        const groupList = [entity, ...entities]
            .filter((e) => e !== null)
            .map((e) => {
                return {
                    groupId,
                    ...e,
                    issueId: 'type' in e ? e.id : null,
                    convertibleId: !('type' in e) ? e.id : null,
                } as IssuesGroupEntity
            })
        if (groupList.length < 2) return // ignore if less than two issues
        if (isGroupInList(Object.values(issueGroups), groupList)) return // ignore if group already exists
        const newGroups = { ...issueGroups }
        newGroups[groupId] = groupList
        setFormGroups(newGroups)
        setIssueGroups(newGroups)
        setIsCustomFormDirty(true)
    }
    const handleAddToGroup = (groupId: string) => {
        const newIssueGroups = { ...issueGroups }
        const e =
            entityType === 'issue'
                ? { ...entity, issueId: entity.id, convertibleId: null }
                : { ...entity, convertibleId: entity.id, issueId: null }
        const newGroup = [...newIssueGroups[groupId], { groupId, ...e }]
        if (isGroupInList(Object.values(issueGroups), newGroup)) return // ignore if group already exists
        newIssueGroups[groupId] = newGroup
        setFormGroups(newIssueGroups)
        setIssueGroups(newIssueGroups)
        setIsCustomFormDirty(true)
    }
    const handleDeleteFromGroup = (groupId: string) => {
        const group = issueGroups[groupId]
        if (!group.some((i) => (entityType === 'issue' ? i.issueId : i.convertibleId) === entity.id)) {
            console.log('Entity not in group, wont do anything.')
            return // ignore if not in group
        }
        const newIssueGroups = { ...issueGroups }
        if (group.length <= 2) {
            // delete group if only two issues left
            delete newIssueGroups[groupId]
            setFormGroups(newIssueGroups)
            setIssueGroups(newIssueGroups)
            return
        }
        newIssueGroups[groupId] = newIssueGroups[groupId].filter(
            (e) => e.issueId !== entity.id && e.convertibleId !== entity.id,
        )
        setFormGroups(newIssueGroups)
        setIssueGroups(newIssueGroups)
        setIsCustomFormDirty(true)
    }

    const isGuarantorFeeIssue = entityType === 'issue' && entity.type === 'Guarantor fee issue'
    const hasReachedMaxIssueGroups =
        isGuarantorFeeIssue && Object.values(issueGroups).some((g) => g.some((e) => e.id === entity.id))

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={6}>
                    <AllCompanyIssues
                        handleCreateGroup={handleCreateGroup}
                        hasReachedMaxIssueGroups={hasReachedMaxIssueGroups}
                        issues={issues}
                        convertibles={convertibles}
                        isGuarantorFeeIssue={isGuarantorFeeIssue}
                        isIssuePage={entityType === 'issue'}
                    />
                </Grid>
                <Grid size={6}>
                    <IssueGroups
                        entity={entity}
                        entitiesInGroups={issueGroups}
                        handleDeleteFromGroup={handleDeleteFromGroup}
                        handleAddToGroup={handleAddToGroup}
                        isGuarantorFeeIssue={isGuarantorFeeIssue}
                    />
                </Grid>
            </Grid>
        </>
    )
}

export default LinkedIssuesDetails
